import React, { useEffect, useState, useCallback } from 'react';
import { TreeSelect } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { fetchActiveServices } from '@/redux/actions/serviceActions';
import _ from 'lodash';

// Biến cục bộ toàn cục - dùng để đảm bảo chỉ fetch API 1 lần duy nhất
let hasFetched = false;

/**
 * TreeSelect component dùng để chọn dịch vụ từ Redux và tìm kiếm qua API
 */
const ServiceSelect = ({
    value,
    onChange,
    placeholder = 'Chọn một dịch vụ',
    multiple = false,
    allowClear = true,
    ...restProps
}) => {
    const dispatch = useDispatch();

    /**
     * Lấy danh sách dịch vụ dạng cây từ Redux store
     */
    const serviceParentTree = useSelector(
        state => state.serviceReducer?.activeList?.items || []
    );

    /**
     * Trạng thái loading khi đang tìm kiếm dịch vụ qua API
     */
    const [loading, setLoading] = useState(false);

    /**
     * Danh sách các node mở rộng trong TreeSelect
     */
    const [expandedKeys, setExpandedKeys] = useState([]);

    /**
     * Đệ quy duyệt toàn bộ cây dịch vụ để lấy ra danh sách id của node có children
     *
     * @param nodes - danh sách cây dịch vụ
     * @returns danh sách expandedKeys
     */
    const collectExpandedKeys = useCallback((nodes) => {
        let keys = [];
        for (let node of nodes) {
            if (node.children?.length) {
                keys.push(node.value);
                keys = keys.concat(collectExpandedKeys(node.children));
            }
        }
        return keys;
    }, []);

    /**
     * Khi component mount lần đầu, nếu chưa từng fetch và chưa có dữ liệu → gọi API
     */
    useEffect(() => {
        if (!hasFetched && !serviceParentTree.length) {
            hasFetched = true;
            dispatch(fetchActiveServices());
        }
    }, [dispatch, serviceParentTree.length]);

    /**
     * Khi serviceParentTree thay đổi → mở sẵn các node có children
     */
    useEffect(() => {
        if (serviceParentTree?.length) {
            setExpandedKeys(collectExpandedKeys(serviceParentTree));
        }
    }, [serviceParentTree, collectExpandedKeys]);

    /**
     * Gọi API tìm kiếm dịch vụ từ từ khóa (debounced 400ms)
     * Dùng lodash.debounce để tránh gọi API quá nhanh
     *
     * @param keyword - từ khóa tìm kiếm
     */
    const debouncedSearch = useCallback(
        _.debounce(async (keyword) => {
            setLoading(true);
            await dispatch(fetchActiveServices({ keyword }));
            setLoading(false);
        }, 400),
        [dispatch]
    );

    /**
     * Gọi khi người dùng nhập từ khóa tìm kiếm
     */
    const handleSearch = (val) => {
        debouncedSearch(val);
    };

    return (
        <TreeSelect
            style={{ width: '100%' }}
            value={value}
            onChange={onChange}
            treeData={serviceParentTree}
            multiple={multiple}
            allowClear={allowClear}
            dropdownStyle={{ maxHeight: 450, overflow: 'auto' }}
            showSearch
            onSearch={handleSearch}
            filterTreeNode={false}
            treeExpandedKeys={expandedKeys}
            onTreeExpand={setExpandedKeys}
            treeDefaultExpandAll={false}
            treeNodeFilterProp="title"
            placeholder={placeholder}
            loading={loading}
            {...restProps}
        />
    );
};

export default React.memo(ServiceSelect);